import type { CSSProperties } from 'react'
import type { ForecastState } from '../../types'
import { useTranslation } from '../../lib/i18n'

interface ProgressBarProps {
  state: ForecastState
}

const NEXT_HORIZON_KEYS: Array<{ threshold: number; key: string }> = [
  { threshold: 20, key: 'progress.label_6m' },
  { threshold: 30, key: 'progress.label_1y' },
]

export function ProgressBar({ state }: ProgressBarProps) {
  const { t } = useTranslation()
  const { next_horizon_in, progress_bar } = state.meta
  const { series_count } = state.current

  if (next_horizon_in === null && state.status !== 'WARMUP') return null

  if (state.status === 'WARMUP') {
    const total = 14
    const filled = Math.min(series_count, total)
    const pct = (filled / total) * 100
    const left = total - filled

    return (
      <div style={styles.root}>
        <div style={styles.rowBig}>
          <span style={styles.hint}>{t('progress.title')}</span>
          <span style={styles.count}>{filled} / {total}</span>
        </div>
        <div style={styles.trackBig}>
          <div style={{ ...styles.fill, width: `${pct}%` }} />
        </div>
        <div style={styles.sub}>{t('progress.sub', { left })}</div>
      </div>
    )
  }

  if (next_horizon_in === null) return null
  const pct = progress_bar * 100
  const horizonKey = NEXT_HORIZON_KEYS.find((x) => series_count < x.threshold)?.key ?? ''
  const nextLabel = horizonKey ? t(horizonKey) : ''

  return (
    <div style={styles.root}>
      <div style={styles.rowSmall}>
        <span style={styles.hint}>{nextLabel} · {t('progress.sub', { left: next_horizon_in })}</span>
      </div>
      <div style={styles.trackSmall}>
        <div style={{ ...styles.fill, width: `${pct}%` }} />
      </div>
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  root: { padding: '12px 0' },
  rowBig: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  rowSmall: { marginBottom: 4 },
  hint: {
    fontSize: 13,
    color: 'var(--tg-theme-hint-color, #888)',
  },
  count: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--tg-theme-text-color, #000)',
  },
  trackBig: {
    height: 6,
    borderRadius: 3,
    background: 'var(--tg-theme-secondary-bg-color, #e5e7eb)',
    overflow: 'hidden',
    marginBottom: 4,
  },
  trackSmall: {
    height: 3,
    borderRadius: 2,
    background: 'var(--tg-theme-secondary-bg-color, #e5e7eb)',
    overflow: 'hidden',
  },
  fill: {
    height: '100%',
    background: 'var(--tg-theme-button-color, #007aff)',
    borderRadius: 'inherit',
    transition: 'width 0.3s ease',
  },
  sub: {
    fontSize: 12,
    color: 'var(--tg-theme-hint-color, #888)',
  },
}
